import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Copy, Mail, MessageCircle, Facebook } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Spinner } from "@/components/common";
import { useMeta } from "@/hooks/useMeta";

const API_ROOT = process.env.REACT_APP_BACKEND_URL;

function Share({ s }: any) {
  const url = window.location.href;
  const text = `${s.names} said yes! Read our proposal story`;
  const btn = "inline-flex items-center gap-2 border border-neutral-300 px-4 py-2.5 eyebrow text-[10px] hover:border-neutral-900 transition-colors";
  const copy = async () => { try { await navigator.clipboard.writeText(url); toast("Link copied"); } catch { toast(url); } };
  return (
    <div className="flex flex-wrap justify-center gap-2 mt-8" data-testid="story-share">
      <button onClick={copy} className={btn} data-testid="story-share-copy"><Copy size={13} />Copy link</button>
      <a href={`https://wa.me/?text=${encodeURIComponent(`${text}: ${url}`)}`} target="_blank" rel="noreferrer" className={btn} data-testid="story-share-whatsapp"><MessageCircle size={13} />WhatsApp</a>
      <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`} target="_blank" rel="noreferrer" className={btn} data-testid="story-share-facebook"><Facebook size={13} />Facebook</a>
      <a href={`mailto:?subject=${encodeURIComponent(text)}&body=${encodeURIComponent(`${text}:\n${url}`)}`} className={btn} data-testid="story-share-email"><Mail size={13} />Email</a>
    </div>
  );
}

export default function StoryPage() {
  const { id = "" } = useParams();
  const [s, setS] = useState<any>(null);
  const [err, setErr] = useState("");
  const [img, setImg] = useState("");
  useEffect(() => { api.get(`/stories/${id}`).then((r) => { setS(r.data); setImg(r.data.photos[0] || ""); }).catch((e) => setErr(errMsg(e))); }, [id]);
  useMeta(s ? `${s.names} Said Yes | Somnio.Co` : "Said Yes | Somnio.Co", s ? s.story.slice(0, 150) : "A Somnio.Co proposal story.");
  if (err) return <div className="max-w-xl mx-auto px-5 py-32 text-center" data-testid="story-not-found"><h1 className="font-display text-4xl">Story not found</h1><p className="mt-4 text-sm text-neutral-600">{err}</p></div>;
  if (!s) return <Spinner />;
  return (
    <article className="max-w-[1100px] mx-auto px-5 md:px-12 py-14 md:py-20" data-testid="story-page">
      <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-center">
        <div className="rise">
          {img && <img src={`${API_ROOT}${img}`} alt={s.names} className="w-full aspect-[4/5] object-cover" data-testid="story-page-image" />}
          {s.photos.length > 1 && <div className="mt-3 flex gap-2">{s.photos.map((p: string, j: number) => <button key={p} onClick={() => setImg(p)} className={`w-16 h-16 overflow-hidden border ${p === img ? "border-neutral-900" : "border-transparent opacity-60"}`} data-testid={`story-page-thumb-${j}`}><img src={`${API_ROOT}${p}`} alt="" className="w-full h-full object-cover" /></button>)}</div>}
        </div>
        <div className="text-center md:text-left rise [animation-delay:120ms]">
          <p className="eyebrow text-[10px] text-[var(--c-primary)]">They Said Yes</p>
          <h1 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl mt-4" data-testid="story-page-names">{s.names}</h1>
          {s.place && <p className="eyebrow text-[11px] text-neutral-500 mt-3">{s.place}</p>}
          <p className="mt-8 text-base leading-relaxed text-neutral-700 whitespace-pre-line" data-testid="story-page-text">{s.story}</p>
          <Share s={s} />
        </div>
      </div>
      <div className="text-center mt-16 border-t border-neutral-200 pt-10">
        <p className="font-display text-2xl">Planning your own proposal?</p>
        <div className="flex flex-wrap justify-center gap-4 mt-5">
          <Link to="/pages/concierge-surprise-proposals#share-your-story" className="eyebrow text-[11px] link-u" data-testid="story-page-more">More love stories</Link>
          <Link to="/pages/loan-ring-how-it-works" className="eyebrow text-[11px] link-u">Free loan ring</Link>
          <Link to="/consultation?kind=concierge&surprise=1" className="eyebrow text-[11px] link-u">Plan a surprise proposal</Link>
        </div>
      </div>
    </article>
  );
}
